import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import "./SidePannelAdmin.css";
import PromptAutomatorMaster from "../PromptAutomator/PromptAutomatorMaster";
import ManageUsers from "../Users/ManageUsers";
import SimpleSidebar from "./SimpleSidebar";
import HelpCenter from "../Helpcenter/HelpCenter";
import { useAgency } from '../AgencyContext';
import {
  AutoAwesome,
  Group,
  AccountCircle,
  Help,
  Logout,
  Close,
  Edit,
} from "@mui/icons-material";
import VideoLabel from "@mui/icons-material/VideoLabel";
import { useAuthState } from 'react-firebase-hooks/auth';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase.config.js';
import iconsidemenu from "../img/iconsidemenu.png";
import { getDocs, addDoc, serverTimestamp, updateDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase.config.js';

function SidePannelAdmin({ taskView }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("promptautomator");
  const [selectedModel, setSelectedModel] = useState("");
  const { currentAgency, getAgencyCollection } = useAgency();
  const [user] = useAuthState(auth);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [models, setModels] = useState([]);

  // Navigation function
  const navigateToSection = (section) => {
    setActiveSection(section);
    
    
    if (section === 'promptautomator') {
      navigate('/');
    } else {
      navigate(`/${section}`);
    }
  };

  // Detect route and set active section
  useEffect(() => {
    const path = location.pathname.slice(1); // Remove leading slash
    if (path === '' || path === 'admin') {
      setActiveSection('promptautomator');
    } else {
      setActiveSection(path);
    }
  }, [location.pathname]);

  // Sauvegarder le profil dans localStorage
  const saveUserProfile = (modelId) => {
    if (user?.email) {
      const key = `selectedProfile_${user.email}`;
      localStorage.setItem(key, modelId);
      console.log(`Profil sauvegardé pour ${user.email}: ${modelId}`);
    }
  };

  // Charger le profil au démarrage
  useEffect(() => {
    if (user?.email) {
      const key = `selectedProfile_${user.email}`;
      const savedProfile = localStorage.getItem(key);
      
      console.log(`Chargement profil pour ${user.email}:`, savedProfile);
      
      if (savedProfile) {
        setSelectedModel(savedProfile);
      }
    }
  }, [user?.email]);

  // Fonction pour gérer le changement de modèle avec sauvegarde
  const handleModelChange = (modelId) => {
    setSelectedModel(modelId);
    saveUserProfile(modelId);
    // Force refresh de la page pour éviter les problèmes de cache entre profils
    window.location.reload();
  };

  // Profile menu functions
  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
  };

  const closeProfileMenu = () => {
    setIsProfileMenuOpen(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error during sign out:', error);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
    // Fermer le menu profil si ouvert
    setIsProfileMenuOpen(false);
  };

  // Charger les modèles IA
  useEffect(() => {
    const loadModels = async () => {
      try {
        if (!user || !currentAgency?.key) {
          return;
        }

        const aimodelCollection = getAgencyCollection('aimodel');
        if (!aimodelCollection) {
          return;
        }

        const querySnapshot = await getDocs(aimodelCollection);
        const modelsData = querySnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(model => model.status !== 'deleted');

        setModels(modelsData);
      } catch (error) {
        console.error('Error loading models:', error);
      }
    };

    loadModels();
  }, [user, currentAgency?.key, getAgencyCollection]);

  // Auto-sélectionner le premier profil si aucun n'est sélectionné
  useEffect(() => {
    if (models.length > 0 && !selectedModel) {
      // Vérifier si un profil est sauvegardé dans localStorage
      const key = user?.email ? `selectedProfile_${user.email}` : null;
      const savedProfile = key ? localStorage.getItem(key) : null;

      // Vérifier si le profil sauvegardé existe dans les modèles
      const savedExists = savedProfile && models.some(m => m.id === savedProfile);

      if (savedExists) {
        setSelectedModel(savedProfile);
      } else {
        // Sinon, sélectionner le premier profil
        setSelectedModel(models[0].id);
        if (key) {
          localStorage.setItem(key, models[0].id);
        }
      }
    }
  }, [models, selectedModel, user?.email]);

  // Gestion de l'upload de photo
  const handlePhotoChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('File size should be less than 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    setNewModelPhoto(file);
    // Créer une URL de prévisualisation
    const previewUrl = URL.createObjectURL(file);
    setNewModelPhotoUrl(previewUrl);
  };

  // Upload de la photo vers Firebase Storage
  const uploadPhoto = async () => {
    if (!newModelPhoto || !user) return null;

    try {
      const timestamp = Date.now();
      const fileName = `aimodel_photos/${user.uid}/${timestamp}_${newModelPhoto.name}`;
      const photoRef = ref(storage, fileName);
      
      await uploadBytes(photoRef, newModelPhoto);
      const photoURL = await getDownloadURL(photoRef);
      
      return photoURL;
    } catch (error) {
      console.error('Error uploading photo:', error);
      throw error;
    }
  };

  // Ajouter un nouveau profil
  const handleAddProfile = async () => {
    if (!newModelName.trim() || !user) return;

    try {
      setUploadingPhoto(true);
      
      let photoURL = null;
      if (newModelPhoto) {
        photoURL = await uploadPhoto();
      }

      const aimodelCollection = getAgencyCollection('aimodel');
      if (!aimodelCollection) return;

      await addDoc(aimodelCollection, {
        name: newModelName.trim(),
        photoURL: photoURL,
        createdAt: serverTimestamp(),
        createdBy: user.uid
      });

      // Reset form
      setNewModelName('');
      setNewModelPhoto(null);
      setNewModelPhotoUrl('');
      setShowAddForm(false);
      setUploadingPhoto(false);

      // Recharger les modèles
      const querySnapshot = await getDocs(aimodelCollection);
      const modelsData = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(model => model.status !== 'deleted');
      setModels(modelsData);

    } catch (error) {
      console.error('Error adding profile:', error);
      setUploadingPhoto(false);
    }
  };

  // Delete profile functions
  const openDeleteModal = (profile) => {
    setProfileToDelete(profile);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setProfileToDelete(null);
  };

  const handleDeleteProfile = async () => {
    if (!profileToDelete || !user) return;

    try {
      const aimodelCollection = getAgencyCollection('aimodel');
      if (!aimodelCollection) return;

      const profileRef = doc(aimodelCollection, profileToDelete.id);
      await updateDoc(profileRef, {
        status: 'deleted',
        deletedAt: serverTimestamp(),
        deletedBy: user.uid
      });

      // Recharger les modèles
      const querySnapshot = await getDocs(aimodelCollection);
      const modelsData = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(model => model.status !== 'deleted');
      setModels(modelsData);

      // Si le profil supprimé était sélectionné, le désélectionner
      if (selectedModel === profileToDelete.id) {
        setSelectedModel('');
        saveUserProfile('');
      }

      closeDeleteModal();
    } catch (error) {
      console.error('Error deleting profile:', error);
    }
  };

  // Edit profile functions
  const openEditModal = (profile) => {
    setProfileToEdit(profile);
    setEditModelName(profile.name);
    setEditModelPhotoUrl(profile.photoURL || '');
    setEditModelPhoto(null);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setProfileToEdit(null);
    setEditModelName('');
    setEditModelPhoto(null);
    setEditModelPhotoUrl('');
  };

  const handleEditPhotoChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('File size should be less than 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    setEditModelPhoto(file);
    // Créer une URL de prévisualisation
    const previewUrl = URL.createObjectURL(file);
    setEditModelPhotoUrl(previewUrl);
  };

  const uploadEditPhoto = async () => {
    if (!editModelPhoto || !user) return null;

    try {
      const timestamp = Date.now();
      const fileName = `aimodel_photos/${user.uid}/${timestamp}_${editModelPhoto.name}`;
      const photoRef = ref(storage, fileName);
      
      await uploadBytes(photoRef, editModelPhoto);
      const photoURL = await getDownloadURL(photoRef);
      
      return photoURL;
    } catch (error) {
      console.error('Error uploading photo:', error);
      throw error;
    }
  };

  const handleEditProfile = async () => {
    if (!editModelName.trim() || !profileToEdit || !user) return;

    try {
      setUploadingPhoto(true);
      
      let photoURL = editModelPhotoUrl;
      if (editModelPhoto) {
        photoURL = await uploadEditPhoto();
      }

      const aimodelCollection = getAgencyCollection('aimodel');
      if (!aimodelCollection) return;

      const profileRef = doc(aimodelCollection, profileToEdit.id);
      await updateDoc(profileRef, {
        name: editModelName.trim(),
        photoURL: photoURL,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid
      });

      // Recharger les modèles
      const querySnapshot = await getDocs(aimodelCollection);
      const modelsData = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(model => model.status !== 'deleted');
      setModels(modelsData);

      closeEditModal();
    } catch (error) {
      console.error('Error editing profile:', error);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const menuItems = [
    {
      id: "promptautomator",
      title: "Prompting",
      icon: <AutoAwesome />,
      description: "AI prompt management",
      component: <PromptAutomatorMaster selectedModel={selectedModel} />,
      active: activeSection === "promptautomator",
      onClick: navigateToSection,
      badge: 0
    },
  ];

  const activeItem = menuItems.find((item) => item.id === activeSection);
  const selectedModelData = models.find(model => model.id === selectedModel);
  
  // Function to get content for modal sections (Users, Help)
  const getModalSectionContent = () => {
    switch(activeSection) {
      case 'manageuser':
        return <ManageUsers />;
      case 'help':
        return <HelpCenter />;
      default:
        return null;
    }
  };

  // Gérer l'ouverture du menu de switch profile
  const [isSwitchProfileOpen, setIsSwitchProfileOpen] = useState(false);
  const switchProfileRef = useRef(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newModelName, setNewModelName] = useState('');
  const [newModelPhoto, setNewModelPhoto] = useState(null);
  const [newModelPhotoUrl, setNewModelPhotoUrl] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [profileToEdit, setProfileToEdit] = useState(null);
  const [editModelName, setEditModelName] = useState('');
  const [editModelPhoto, setEditModelPhoto] = useState(null);
  const [editModelPhotoUrl, setEditModelPhotoUrl] = useState('');
  const [userImageLoaded, setUserImageLoaded] = useState(false);
  const [switchProfileImageLoaded, setSwitchProfileImageLoaded] = useState(false);
  const [profileImagesLoaded, setProfileImagesLoaded] = useState({});
  
  const toggleSwitchProfile = () => {
    // Si le dropdown est ouvert, le fermer. Sinon, l'ouvrir.
    if (isSwitchProfileOpen) {
      setIsSwitchProfileOpen(false);
    } else {
      setIsSwitchProfileOpen(true);
    }
  };

  // Fermer le dropdown de switch profile quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Vérifier si le clic est sur le bouton de profil (éviter le conflit)
      const isProfileButton = event.target.closest('.switch-profile-avatar-img, .switch-profile-avatar-placeholder, .simple-label, .profile-name-skeleton') ||
                             event.target.classList.contains('switch-profile-avatar-img') ||
                             event.target.classList.contains('switch-profile-avatar-placeholder') ||
                             event.target.classList.contains('simple-label') ||
                             event.target.classList.contains('profile-name-skeleton');
      
      if (switchProfileRef.current && !switchProfileRef.current.contains(event.target) && !isProfileButton) {
        setIsSwitchProfileOpen(false);
      }
    };

    if (isSwitchProfileOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isSwitchProfileOpen]);

  // Reset image loaded state when selectedModelData changes
  useEffect(() => {
    setSwitchProfileImageLoaded(false);
  }, [selectedModelData?.photoURL]);

  // Initialize profile images loaded state for profiles without photos
  useEffect(() => {
    const initialState = {};
    models.forEach(model => {
      if (!model.photoURL) {
        initialState[model.id] = true; // Profiles without photos are considered "loaded"
      }
    });
    setProfileImagesLoaded(initialState);
  }, [models]);

  // Check if all profile images are loaded
  const areAllProfileImagesLoaded = models.length > 0 && models.every(model => 
    !model.photoURL || profileImagesLoaded[model.id]
  );

  return (
    <div className="admin">
      <div className="admin__container">
        {/* Simple Fixed Sidebar */}
        <SimpleSidebar
          isCollapsed={isSidebarCollapsed}
          onToggle={toggleSidebar}
          logo={{
            src: iconsidemenu,
            expandIcon: <VideoLabel style={{ color: '#AFAFAF', transform: 'rotate(90deg) scaleX(0.8)', fontSize: '16px' }} />
          }}
          menuItems={menuItems}
          switchProfile={{
            avatar: selectedModelData?.photoURL ? (
              <>
                {!switchProfileImageLoaded && <div className="profile-avatar-skeleton"></div>}
                <img 
                  src={selectedModelData.photoURL} 
                  alt={selectedModelData.name}
                  className="switch-profile-avatar-img"
                  onLoad={() => setSwitchProfileImageLoaded(true)}
                  style={{ display: switchProfileImageLoaded ? 'block' : 'none' }}
                />
              </>
            ) : selectedModelData?.name ? (
              <div className="switch-profile-avatar-placeholder">
                {selectedModelData.name.charAt(0).toUpperCase()}
              </div>
            ) : null,
            name: selectedModelData?.photoURL ? (
              switchProfileImageLoaded ? selectedModelData?.name : null
            ) : selectedModelData?.name,
            onClick: toggleSwitchProfile
          }}
          profileButton={
            <button className="gmail-profile-button" onClick={toggleProfileMenu}>
              {user ? (
                user.photoURL ? (
                  <>
                    {!userImageLoaded && <div className="user-profile-skeleton"></div>}
                    <img 
                      src={user.photoURL} 
                      alt="Profile" 
                      className="gmail-profile-image"
                      referrerPolicy="no-referrer"
                      onLoad={() => setUserImageLoaded(true)}
                      style={{ display: userImageLoaded ? 'block' : 'none' }}
                    />
                  </>
                ) : (
                  <div className="gmail-profile-placeholder">
                    <AccountCircle className="gmail-profile-icon" /> 
                  </div>
                )
              ) : (
                <div className="user-profile-skeleton"></div>
              )}
            </button>
          }
          profileName={user?.displayName || user?.email?.split('@')[0] || 'User'}
        />

        {/* Profile Menu Portal */}
        {isProfileMenuOpen && ReactDOM.createPortal(
          <div className="admin__modal__overlay" onClick={closeProfileMenu}>
            <div className="admin__modal__content" onClick={(e) => e.stopPropagation()}>
              <div className="admin__modal__header">
                <div className="admin__modal__user-info">
                  <div className="admin__modal__user-details">
                    <h3>{user.email}</h3>
                    <div className="admin__modal__user-role">
                      {currentAgency?.userRole}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="admin__modal__actions">
                <div className="admin__modal__agency">
                  <span className="admin__modal__agency-label">Agency:</span>
                  <span className="admin__modal__agency-name">{currentAgency?.name || 'No Agency'}</span>
                </div>
                
                
                <button 
                  className="admin__modal__button" 
                  onClick={() => { setActiveSection('manageuser'); closeProfileMenu(); }}
                >
                  <Group className="admin__modal__button-icon" />
                  <span>Users</span>
                </button>
                
                <button 
                  className="admin__modal__button" 
                  onClick={() => { setActiveSection('help'); closeProfileMenu(); }}
                >
                  <Help className="admin__modal__button-icon" />
                  <span>Help</span>
                </button>
                
                <button 
                  className="admin__modal__button admin__modal__button--logout" 
                  onClick={() => { handleSignOut(); closeProfileMenu(); }}
                >
                  <Logout className="admin__modal__button-icon" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Switch Profile Dropdown */}
        {isSwitchProfileOpen && (
          <div className="switch-profile-dropdown" ref={switchProfileRef}>
            <div className="switch-profile-list">
              {models.map((model) => (
                <div
                  key={model.id}
                  className={`switch-profile-item ${selectedModel === model.id ? 'active' : ''}`}
                >
                  <div 
                    className="switch-profile-content"
                    onClick={() => {
                      setSelectedModel(model.id);
                      handleModelChange(model.id);
                      setIsSwitchProfileOpen(false);
                    }}
                  >
                    <div className="switch-profile-avatar">
                      {model.photoURL ? (
                        <>
                          {!areAllProfileImagesLoaded && <div className="profile-avatar-skeleton"></div>}
                          <img 
                            src={model.photoURL} 
                            alt={model.name}
                            onLoad={() => setProfileImagesLoaded(prev => ({...prev, [model.id]: true}))}
                            style={{ display: areAllProfileImagesLoaded ? 'block' : 'none' }}
                          />
                        </>
                      ) : (
                        <div className="switch-profile-placeholder">
                          {model.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <span className="switch-profile-name">
                      {model.photoURL ? (
                        areAllProfileImagesLoaded ? model.name : <div className="profile-name-skeleton"></div>
                      ) : (
                        model.name
                      )}
                    </span>
                    {selectedModel === model.id && (
                      <div className="switch-profile-check">✓</div>
                    )}
                  </div>
                  <div className="switch-profile-actions">
                    <button 
                      className="switch-profile-edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(model);
                      }}
                      title="Edit profile"
                    >
                      <Edit />
                    </button>
                    <button 
                      className="switch-profile-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDeleteModal(model);
                      }}
                      title="Delete profile"
                    >
                      <Close />
                    </button>
                  </div>
                </div>
              ))}
              
              {/* Add Profile Option */}
              <div className="switch-profile-divider"></div>
              <div 
                className="switch-profile-item switch-profile-add"
                onClick={() => {
                  setShowAddForm(true);
                  setIsSwitchProfileOpen(false);
                }}
              >
                <div className="switch-profile-avatar">
                  <div className="switch-profile-add-icon">+</div>
                </div>
                <span className="switch-profile-name">Add Profile</span>
              </div>
            </div>
          </div>
        )}

        {/* Add Profile Modal */}
        {showAddForm && (
          <div className="add-profile-modal-overlay" onClick={() => setShowAddForm(false)}>
            <div className="add-profile-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Add New Profile</h3>
              
              <div className="add-profile-photo">
                <div className="photo-preview">
                  {newModelPhotoUrl ? (
                    <img src={newModelPhotoUrl} alt="Preview" />
                  ) : (
                    <div className="photo-placeholder">📷</div>
                  )}
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  style={{ display: 'none' }}
                  id="photo-input"
                />
                <button 
                  className="photo-btn"
                  onClick={() => document.getElementById('photo-input').click()}
                >
                  {newModelPhotoUrl ? 'Change Photo' : 'Add Photo'}
                </button>
              </div>

              <input
                type="text"
                placeholder="Profile name"
                value={newModelName}
                onChange={(e) => setNewModelName(e.target.value)}
                className="add-profile-input"
              />

              <div className="add-profile-actions">
                <button 
                  className="btn-cancel"
                  onClick={() => {
                    setShowAddForm(false);
                    setNewModelName('');
                    setNewModelPhoto(null);
                    setNewModelPhotoUrl('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="btn-save"
                  onClick={handleAddProfile}
                  disabled={!newModelName.trim() || uploadingPhoto}
                >
                  {uploadingPhoto ? 'Adding...' : 'Add Profile'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteModalOpen && ReactDOM.createPortal(
          <div className="add-profile-modal-overlay" onClick={closeDeleteModal}>
            <div className="add-profile-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Profile</h3>
              <p style={{ 
                textAlign: 'center', 
                marginBottom: '20px', 
                color: '#374151',
                fontSize: '14px',
                lineHeight: '1.5'
              }}>
                Are you sure you want to delete "<strong>{profileToDelete?.name}</strong>"?<br/>
                This action cannot be undone.
              </p>
              <div className="add-profile-actions">
                <button 
                  className="btn-cancel"
                  onClick={closeDeleteModal}
                >
                  Cancel
                </button>
                <button 
                  className="btn-save"
                  onClick={handleDeleteProfile}
                  style={{ backgroundColor: '#dc2626' }}
                >
                  Delete Profile
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Edit Profile Modal */}
        {editModalOpen && ReactDOM.createPortal(
          <div className="add-profile-modal-overlay" onClick={closeEditModal}>
            <div className="add-profile-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Edit Profile</h3>
              
              <div className="add-profile-photo">
                <div className="photo-preview">
                  {editModelPhotoUrl ? (
                    <img src={editModelPhotoUrl} alt="Preview" />
                  ) : (
                    <div className="photo-placeholder">📷</div>
                  )}
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleEditPhotoChange}
                  style={{ display: 'none' }}
                  id="edit-photo-input"
                />
                <button 
                  className="photo-btn"
                  onClick={() => document.getElementById('edit-photo-input').click()}
                >
                  {editModelPhotoUrl ? 'Change Photo' : 'Add Photo'}
                </button>
              </div>

              <input
                type="text"
                placeholder="Profile name"
                value={editModelName}
                onChange={(e) => setEditModelName(e.target.value)}
                className="add-profile-input"
              />

              <div className="add-profile-actions">
                <button 
                  className="btn-cancel"
                  onClick={closeEditModal}
                >
                  Cancel
                </button>
                <button 
                  className="btn-save"
                  onClick={handleEditProfile}
                  disabled={!editModelName.trim() || uploadingPhoto}
                >
                  {uploadingPhoto ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Main Content */}
        <main className="admin__main">
          <section className="admin__content">
            <div className="admin__content-wrapper">
              {taskView || activeItem?.component || getModalSectionContent()}
            </div>
          </section>
        </main>
        
      </div>
    </div>
  );
}


export default SidePannelAdmin;
